/**
 * Layer 1 of view-row pagination (design/large-samples.md, "View-row
 * pagination"): the filtered cursor primitive. Reads surviving events from
 * the events sequence; the stats sidecar's per-chunk type counts let it
 * skip non-matching chunks unread — the filter-pushdown mechanism that
 * makes reads proportional to rows emitted, never events skipped.
 */
import type { SequenceReader } from "./chunkStore";
import type { ChunkedEvent, EventChunkStats } from "./types";

export interface OrdinalEvent {
  ordinal: number;
  ev: ChunkedEvent;
}

/** Buffered surviving events per refill — about one screenful. */
const REFILL = 64;

export class FilteredCursor {
  private buffer: OrdinalEvent[] = [];
  private from: number;
  private exhausted = false;

  constructor(
    private store: SequenceReader<ChunkedEvent>,
    private stats: readonly EventChunkStats[],
    start: number,
    /**
     * Survival predicate over event type; span_begin/span_end are the
     * caller's job to force-include (structure drives the walk).
     */
    private survives: (type: string) => boolean
  ) {
    this.from = start;
  }

  get done(): boolean {
    return this.buffer.length === 0 && this.exhausted;
  }

  private chunkSurvivorCount(chunkIdx: number): number {
    const stats = this.stats[chunkIdx];
    if (!stats) {
      return 1; // no stats — must read
    }
    return Object.entries(stats.type_counts).reduce(
      (n, [type, count]) => n + (this.survives(type) ? count : 0),
      0
    );
  }

  statsFor(chunkIdx: number): EventChunkStats | undefined {
    return this.stats[chunkIdx];
  }

  chunkIndexOf(ordinal: number): number {
    return this.store.chunkIndexOf(ordinal);
  }

  chunkBounds(chunkIdx: number): [number, number] {
    return this.store.chunkBounds(chunkIdx);
  }

  private async refill(): Promise<void> {
    while (this.buffer.length < REFILL && !this.exhausted) {
      if (this.from >= this.store.knownCount) {
        this.exhausted = true;
        return;
      }
      const chunkIdx = this.store.chunkIndexOf(this.from);
      const [lo, hi] = this.chunkBounds(chunkIdx);
      if (this.chunkSurvivorCount(chunkIdx) === 0) {
        this.from = hi; // pushdown: skip the chunk unread
        continue;
      }
      const items = await this.store.loadChunk(chunkIdx);
      for (let i = this.from - lo; i < items.length; i++) {
        const ev = items[i];
        if (ev !== undefined && this.survives(ev.event)) {
          this.buffer.push({ ordinal: lo + i, ev });
        }
      }
      this.from = hi;
    }
  }

  async peek(): Promise<OrdinalEvent | null> {
    if (this.buffer.length === 0) {
      await this.refill();
    }
    return this.buffer[0] ?? null;
  }

  async next(): Promise<OrdinalEvent | null> {
    const head = await this.peek();
    if (head) {
      this.buffer.shift();
    }
    return head;
  }

  /** Jump to `ordinal`: advance in-buffer if already buffered, else drop and refetch. */
  seek(ordinal: number): void {
    const bufferLast = this.buffer.at(-1);
    if (bufferLast !== undefined && bufferLast.ordinal >= ordinal) {
      let head = this.buffer[0];
      while (head !== undefined && head.ordinal < ordinal) {
        this.buffer.shift();
        head = this.buffer[0];
      }
      return;
    }
    this.buffer = [];
    this.exhausted = false;
    this.from = Math.max(this.from, ordinal);
  }
}
